// plugins/deep-link.client.ts (updated)
import { Capacitor } from '@capacitor/core'
import { App as CapacitorApp } from '@capacitor/app'
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin'
import consola from 'consola'
import { defineNuxtPlugin, useRouter } from '#app'
import { parseDeepLink, mayAcceptSessionFromDeepLink } from '@shared/lib'
import { captureUtmFromUrl } from '@shared/lib/analytics'

/**
 * Safely opens / replaces the Reset-Password modal even when another
 * modal is already displayed.
 */
const openResetPasswordModal = (
    modalStore: any,
    modalStateEnum: Record<string, any>,
) => {
  // If another modal is already displayed, try to replace it.
  if (modalStore.isOpen) {
    // Prefer a force-switch API (if the store implements one).
    try {
      modalStore.open?.(modalStateEnum.ResetPassword, { force: true })
      return
    } catch {
      // Fallback: close then re-open.
      modalStore.close?.()
    }
  }
  modalStore.open(modalStateEnum.ResetPassword)
}

export default defineNuxtPlugin(() => {
  const router = useRouter()

  /**
   * Main reset-password flow: verifies the token, stores data in Pinia modal
   * store and ensures the corresponding modal is shown.
   */
  const handleResetPasswordFromDeepLink = async (token: string) => {
    try {
      consola.log('[DL] Processing reset-password token:', token)

      // Dynamic imports (tree-shakable in SSR)
      const [{ useModalStore }, { modalStateEnum }, APIAuth] = await Promise.all([
        import('@shared/model'),
        import('~/utils/enums/modalStateEnum'),
        import('~/entities/auth/api'),
      ])

      const modalStore = useModalStore()

      // 1️⃣ Verify token with backend
      const { data } = await APIAuth.default.verifyResetToken({ token })
      if (!data?.user) throw new Error('Invalid reset-password token')

      // 2️⃣ Write token & user into modalStore for the ResetPassword component
      modalStore.updateModalData({ token, user: data.user })

      // 3️⃣ Open / replace modal (small timeout for smoother transitions)
      setTimeout(() => openResetPasswordModal(modalStore, modalStateEnum), 100)
    } catch (error: any) {
      consola.error('[DL] reset-password error:', error)
      alert('Недействительная ссылка или произошла ошибка при проверке токена')
    }
  }

  /**
   * Generic URL processor for deep links coming from both `appUrlOpen` and
   * `getLaunchUrl()` (when the app is resumed from the background).
   */
  const processDeepLinkUrl = async (rawUrl?: string) => {
    if (!rawUrl) return

    // Адрес приходит извне (ОС, адресная строка) и может не разбираться вовсе —
    // например, если в схеме есть подчёркивание, недопустимое по грамматике URI.
    let url: URL
    try {
      url = new URL(rawUrl)
    } catch {
      consola.log('[DL] Не удалось разобрать адрес:', rawUrl)
      return
    }

    consola.log('[DL] Processing URL:', url.href)

    // Рекламные метки из диплинка: в нативной сборке ссылка запуска не попадает в
    // window.location, и захват из analytics-плагина её не видит — без этого вызова
    // атрибуция рекламы в приложении не работала бы вовсе.
    try {
      if (captureUtmFromUrl(url.search)) consola.log('[DL] Зафиксированы метки кампании')
    } catch {
      // Аналитика не имеет права ломать обработку диплинка.
    }

    // Возврат с оплаты (Universal/App Link): банк редиректит на https .../success?orderId=X,
    // ОС открывает приложение с этой ссылкой. Роутим SPA на нужный путь — success.vue сам
    // сверит оплату по orderId и очистит корзину (источник истины об оплате — webhook на бэке).
    if (url.pathname.includes('/success') || url.pathname.includes('/fail')) {
      const orderId = url.searchParams.get('orderId')
      const basePath = url.pathname.includes('/fail') ? '/fail' : '/success'
      const target = orderId ? `${basePath}?orderId=${encodeURIComponent(orderId)}` : basePath
      consola.log('[DL] Возврат с оплаты →', target)
      await router.replace(target)
      return
    }

    // Маршрут определяем по адресу, а НЕ поиском подстроки в целом href:
    // прежняя проверка срабатывала и на «?x=auth-callback», то есть на любом адресе,
    // куда эти слова подставил кто угодно.
    const parsed = parseDeepLink(url.href)

    if (parsed.route !== 'auth-callback' && parsed.route !== 'reset-password') {
      consola.log('[DL] URL not supported:', url.href)
      return
    }

    if (parsed.route === 'reset-password') {
      const token = url.searchParams.get('token')
      if (token) await handleResetPasswordFromDeepLink(token)
      return
    }

    // auth-callback ----------------------------------------------------------
    if (!mayAcceptSessionFromDeepLink(parsed)) {
      consola.warn('[DL] auth-callback с токенами в обычной ссылке — игнорируем')
      return
    }

    const access  = url.searchParams.get('access_token')
    const refresh = url.searchParams.get('refresh_token')
    if (!access || !refresh) {
      consola.log('[DL] Missing tokens in auth-callback URL')
      return
    }

    consola.log('[DL] Processing auth-callback with tokens')

    try {
      // 1️⃣ Properly persist session to app storage (V2 with sessionId)
      const { useSessionManager } = await import('~/entities/auth')
      const sessionManager = useSessionManager()
      // Note: deep links should pass sessionId instead of refresh token
      // For now, storing access token only (refresh handled by HttpOnly cookies)
      sessionManager.authAccessToken.value = access
      consola.log('[DL] Session persisted via session manager (V2)')

      // 2️⃣ Also mirror to secure storage for native persistence (only on native platforms)
      if (Capacitor.isNativePlatform()) {
        await SecureStoragePlugin.set({ key: 'authAccessToken',  value: access  })
        await SecureStoragePlugin.set({ key: 'authRefreshToken', value: refresh })
        consola.log('[DL] Tokens saved to secure storage')
      }

      // 3️⃣ Import and update user profile
      const { useUserProfileStore } = await import('@entities/user')
      const userProfileStore = useUserProfileStore()
      
      // Fetch user data to update the store
      await userProfileStore.fetchUser()
      consola.log('[DL] User profile updated')

      // 3.5️⃣ Initialize push notifications after successful authentication
      try {
        const pushSettingsModule = await import('@features/notifications')
        const pushSettings = pushSettingsModule.usePushNotificationSettings()
        await pushSettings.initPushNotificationsAfterAuth()
        consola.log('[DL] Push notifications initialized')
      } catch (error) {
        consola.warn('[DL] Failed to initialize push notifications:', error)
      }

      // 4️⃣ Close any open modals (like login modal)
      const { useModalStore } = await import('@shared/model')
      const modalStore = useModalStore()
      if (modalStore.isOpen) {
        consola.log('[DL] Closing open modal')
        modalStore.close()
      }

      // 5️⃣ Navigate to root (only if not already there)
      const currentRoute = router.currentRoute.value
      if (currentRoute.path === '/') {
        consola.log('[DL] Already on root, skipping navigation')
      } else {
        consola.log('[DL] Navigating to root')
        await router.replace('/')
      }

      consola.log('[DL] Auth-callback processed successfully')
    } catch (error) {
      consola.error('[DL] Error processing auth-callback:', error)
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  //  📲  Capacitor listeners (only on native platforms)
  // ──────────────────────────────────────────────────────────────────────────

  if (Capacitor.isNativePlatform()) {
    // Fires on cold start *and* when the app receives a link while running
    CapacitorApp.addListener('appUrlOpen', ({ url }) => {
      consola.log('[DL] appUrlOpen event received:', url)
      processDeepLinkUrl(url)
    })

    // Handles the case where the app was backgrounded and is resumed by Android
    // with an already-consumed appUrlOpen event.
    CapacitorApp.addListener('resume', async () => {
      consola.log('[DL] App resumed, checking for launch URL')
      const launch = await CapacitorApp.getLaunchUrl()
      if (launch?.url) {
        consola.log('[DL] resume -> getLaunchUrl():', launch.url)
        processDeepLinkUrl(launch.url)
      }
    })

    // Optional: observe active / background transitions for debugging
    CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      consola.log('[DL] App state changed:', isActive)
      
      // When app becomes active, check if there's a pending deep link
      if (isActive) {
        consola.log('[DL] App became active, checking for pending deep links')
        // This helps catch deep links that might have been missed
        setTimeout(async () => {
          try {
            const launch = await CapacitorApp.getLaunchUrl()
            if (launch?.url) {
              consola.log('[DL] Found pending launch URL:', launch.url)
              processDeepLinkUrl(launch.url)
            }
          } catch {
            consola.log('[DL] No pending launch URL found')
          }
        }, 500)
      }
    })
  } else {
    consola.log('[DL] Capacitor listeners skipped - running on web platform')
  }

  // ──────────────────────────────────────────────────────────────────────────
  // 🌐  Web fallback (Nuxt dev & PWA)
  // ──────────────────────────────────────────────────────────────────────────
  if (process.client) {
    // Initial check on page load
    processDeepLinkUrl(window.location.href)

    // Listen to client-side navigation / popstate
    window.addEventListener('popstate', () => processDeepLinkUrl(window.location.href))

    // Helper for manual testing in dev-tools:
    //    window.testDeepLink('ru.food_with_character.app://reset-password?...')
    ;(window as any).testDeepLink = (url: string) => {
      consola.log('[DL] testDeepLink:', url)
      processDeepLinkUrl(url)
    }

    // Additional helper for testing auth-callback specifically
    ;(window as any).testAuthCallback = (accessToken: string, refreshToken: string) => {
      consola.log('[DL] testAuthCallback:', { accessToken, refreshToken })
      const url = `ru.food_with_character.app://auth-callback?access_token=${accessToken}&refresh_token=${refreshToken}`
      processDeepLinkUrl(url)
    }

    // Listen for custom events that might be triggered from other parts of the app
    window.addEventListener('deep-link-auth-callback', (event: any) => {
      consola.log('[DL] Custom auth-callback event received:', event.detail)
      if (event.detail?.access_token && event.detail?.refresh_token) {
        const url = `ru.food_with_character.app://auth-callback?access_token=${event.detail.access_token}&refresh_token=${event.detail.refresh_token}`
        processDeepLinkUrl(url)
      }
    })
  }
})
